/**
 * Handle validation utilities
 * 
 * Centralized handle validation rules used by:
 * - Onboarding (primary handle selection)
 * - Settings (handle editing)
 * - Auth (was used, now removed in Phase 3.4)
 * 
 * NO React, NO Supabase calls - pure validation functions only
 */

export const HANDLE_MIN_LENGTH = 3;
export const HANDLE_MAX_LENGTH = 20;

export interface HandleValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates handle format (pure function, no database check)
 * 
 * Rules:
 * - 3-20 characters
 * - Must start and end with letter or number
 * - Can contain letters, numbers, and underscores only
 * - No consecutive underscores
 */
export function validateHandleFormat(handle: string): HandleValidationResult {
  const trimmed = handle.trim().toLowerCase();
  
  if (!trimmed) {
    return { valid: false, error: 'Handle is required' };
  }
  
  if (trimmed.length < HANDLE_MIN_LENGTH) {
    return { valid: false, error: `Handle must be at least ${HANDLE_MIN_LENGTH} characters` };
  }
  
  if (trimmed.length > HANDLE_MAX_LENGTH) {
    return { valid: false, error: `Handle must be ${HANDLE_MAX_LENGTH} characters or less` };
  }
  
  // Must start with letter or number
  if (!/^[a-z0-9]/.test(trimmed)) {
    return { valid: false, error: 'Handle must start with a letter or number' };
  }
  
  // Must end with letter or number
  if (!/[a-z0-9]$/.test(trimmed)) {
    return { valid: false, error: 'Handle must end with a letter or number' };
  }
  
  // Only alphanumeric and underscore allowed (matches database constraint)
  if (!/^[a-z0-9_]+$/.test(trimmed)) {
    return { valid: false, error: 'Handle can only contain letters, numbers, and underscores' };
  }
  
  // No consecutive underscores
  if (/_{2,}/.test(trimmed)) {
    return { valid: false, error: 'Handle cannot have consecutive underscores' };
  }
  
  return { valid: true };
}

/**
 * Detects if a handle was auto-generated by the handle_new_user trigger
 * 
 * Trigger-generated patterns:
 * - "emailprefix_a1b2" (4 hex chars suffix)
 * - "user_xxxxxxxx_xxxx" (uuid-based fallback)
 * 
 * These handles are functional but should prompt user to choose a real one
 */
export function isAutoGeneratedHandle(handle: string): boolean {
  if (!handle) return true;
  
  const trimmed = handle.trim().toLowerCase();
  
  // Pattern 1: temp_ prefix (legacy)
  if (trimmed.startsWith('temp_')) {
    return true;
  }
  
  // Pattern 2: user_ prefix (trigger fallback)
  if (trimmed.startsWith('user_')) {
    return true;
  }
  
  // Pattern 3: Ends with _xxxx where x is hex (trigger email pattern)
  // e.g., "johnsmith_a1b2"
  if (/^.+_[a-f0-9]{4}$/.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Normalizes handle for storage (lowercase, trimmed)
 */
export function normalizeHandle(handle: string): string {
  return handle.trim().toLowerCase();
}
